import React, { useRef, useState } from 'react';
import { useGetPhonicsSoundsQuery } from '@/api/types';
import { useUi, useAssetCdn } from '@/context/Ui';
import { SymbolType, CdnObjectType } from '@/types';
import { AudioPlayer, CdnMedia, RoundPlayButton } from '@/components';
import { findSoundsInString } from '@/utils/sounds';
import { WhitePanel } from '../components/WhitePanel';
import { ActivityTitleStep } from '../components/ActivityTitleStep';
import { Activity, ActivityStep } from '../Activity';
import { ActivityTypeProps } from '../types';

interface ShowRootWordStepProps extends ActivityTypeProps {
  index: number;
  subIndex: number;
  total: number;
  word: string;
}

const ShowRootWordStep = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
  index,
  subIndex,
  total,
  word,
}: ShowRootWordStepProps) => {
  const playerRef = useRef<AudioPlayer>(null);

  const { sendAlert } = useUi();

  const mediaUrl = useAssetCdn(word, SymbolType.Word, CdnObjectType.Audio);

  const [playedOnce, setPlayedOnce] = useState(false);

  const [playerReady, setPlayerReady] = useState(false);

  return (
    <ActivityStep
      stepIdentifier={`${activity.id}-${index}-${subIndex}`}
      activity={activity}
      mode={mode}
      stepNumber={index + 1}
      totalSteps={total}
      wrapperClassName={wrapperClassName}
      isPreview={isPreview}
      title={`Say the word with the suffix.`}
      showTitle={true}
      showActivityPagination={false}
    >
      <RoundPlayButton
        color="white"
        className="my-6"
        onClick={() => {
          playerRef.current?.play();
          setPlayedOnce(true);
        }}
        ariaLabel="Play sound"
        size="sm"
        disabled={!playerReady}
        pressHint={!playedOnce}
      />

      <WhitePanel className="my-6" mode="letter" kind="bigBox">
        <CdnMedia
          className="block mx-auto w-auto h-full"
          symbol={word}
          symbolType={SymbolType.Word}
          objectType={CdnObjectType.Image}
          width={200}
          height={200}
        />
      </WhitePanel>

      <AudioPlayer
        ref={playerRef}
        src={mediaUrl || ''}
        onReadySrc={() => setPlayerReady(true)}
        onLoadTimeoutSrc={() =>
          sendAlert('We could not load some of the audio for this lesson.', {
            type: 'warning',
            title: 'There was a problem',
          })
        }
      />
    </ActivityStep>
  );
};

export const ReadingNewSuffix = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
}: ActivityTypeProps) => {
  if (!activity.words) {
    throw new Error('Words are required for this activity');
  }

  if (!activity.sounds) {
    throw new Error('Sounds are required for this activity');
  }

  const { data: soundsData } = useGetPhonicsSoundsQuery();

  const sounds = findSoundsInString(
    soundsData?.getPhonicsSounds.map((sound) => sound.name) || [],
    activity.sounds,
  );

  const TOTAL_STEPS_PER_WORD = 1 + 1 + (sounds?.length || 0);
  const TOTAL_STEPS = TOTAL_STEPS_PER_WORD * activity.words.length + 1;

  return (
    <Activity
      steps={activity.words?.flatMap((word, index) => [
        <ActivityTitleStep
          key={`${activity.id}-title`}
          activity={activity}
          mode={mode}
          wrapperClassName={wrapperClassName}
          isPreview={isPreview}
          index={0}
          total={TOTAL_STEPS}
        />,
        <ShowRootWordStep
          key={`${activity.id}-${index}-0`}
          activity={activity}
          mode={mode}
          isPreview={isPreview}
          wrapperClassName={wrapperClassName}
          index={index}
          subIndex={0}
          total={TOTAL_STEPS}
          word={word}
        />,
        ...sounds.map((suffix, suffixIndex) => (
          <ShowRootWordStep
            key={`${activity.id}-${index}-${suffixIndex + 1}`}
            activity={activity}
            mode={mode}
            isPreview={isPreview}
            wrapperClassName={wrapperClassName}
            index={index + 1}
            subIndex={suffixIndex + 1}
            total={TOTAL_STEPS}
            word={`${word}${suffix}`}
          />
        )),
      ])}
    />
  );
};
